import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './TransactionForm.css';


const TransactionForm = ({ transaction, initialDate, onSubmit, onCancel, vision, allTags = [], addToast }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    description: '',
    value: '',
    type: 'Despesa',
    category: '',
    status: 'pago',
    date: initialDate || new Date().toISOString().split('T')[0],
    recorrente: false,
    frequencia: 'mensal',
    recorrencia_total: '',
    recorrencia_numero: 1,
    observacoes: '',
    tags: '',
    isBusiness: transaction ? (!!transaction.business_id && transaction.business_id === user?.business_id) : (vision === 'business'),
    comprovante_url: '',
    cartao_id: '',
    recorrencia_id: transaction ? transaction.recorrencia_id : null,
  });

  const [categories, setCategories] = useState([]);
  const [cards, setCards] = useState([]);
  const [loadingCats, setLoadingCats] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { authFetch } = useAuth();

  // Busca categorias da API ao montar o componente
  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCats(true);
      try {
        const res = await authFetch('/api/categories');
        if (res.ok) {
          const data = await res.json();
          setCategories(data);
        }
      } catch (err) {
        console.warn('Não foi possível carregar categorias:', err);
      } finally {
        setLoadingCats(false);
      }
    };
    const fetchCards = async () => {
      try {
        const res = await authFetch('/api/cards');
        if (res.ok) setCards(await res.json());
      } catch (err) {}
    };
    fetchCategories();
    fetchCards();
  }, [authFetch]);

  // Preenche o formulário ao editar
  useEffect(() => {
    if (transaction) {
      const dbStatus = (transaction.status || 'pago').toLowerCase();
      const resolvedStatus = (dbStatus === 'pago' || dbStatus === 'recebido') ? 'pago' : 'pendente';

      setFormData({
        description: transaction.description || transaction.descrição || '',
        value: transaction.value || transaction.valor || '',
        type: transaction.type || transaction.operacao || 'Despesa',
        category: transaction.category || transaction.categoria || '',
        status: resolvedStatus,
        date: transaction.date || transaction.data || new Date().toISOString().split('T')[0],
        recorrente: transaction.recorrente || false,
        frequencia: transaction.frequencia || 'mensal',
        recorrencia_total: transaction.recorrencia_total || '',
        recorrencia_numero: transaction.recorrencia_numero || 1,
        observacoes: transaction.observacoes || '',
        tags: transaction.tags || '',
        isBusiness: !!transaction.business_id && transaction.business_id === user?.business_id,
        comprovante_url: transaction.comprovante_url || '',
        cartao_id: transaction.cartao_id || '',
        recorrencia_id: transaction.recorrencia_id || null,
      });
    }
  }, [transaction]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      // Ao trocar o tipo, limpa a categoria para forçar nova seleção
      if (name === 'type') updated.category = '';
      return updated;
    });
  };

  // Filtra categorias conforme o tipo selecionado — comparação case-insensitive
  const filteredCategories = categories.filter((cat) =>
    (cat.tipo || '').toLowerCase() === (formData.type || '').toLowerCase()
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description || !formData.value) {
      addToast('Por favor, preencha descrição e valor.', 'warning');
      return;
    }

    let uploadedUrl = formData.comprovante_url;
    if (file) {
      setUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      try {
        const uploadRes = await authFetch('/api/uploads', {
          method: 'POST',
          body: fd
        });
        
        if (uploadRes.ok) {
          const data = await uploadRes.json();
          uploadedUrl = data.url;
        } else {
          alert('Falha ao upar arquivo.');
        }
      } catch (e) {
        console.error(e);
      }
      setUploading(false);
    }
    onSubmit({
      description: formData.description,
      value: parseFloat(formData.value),
      type: formData.type,
      category: formData.category,
      status: formData.status,
      date: formData.date,
      recorrente: formData.recorrente,
      frequencia: formData.frequencia,
      recorrencia_total: formData.recorrencia_total ? parseInt(formData.recorrencia_total) : null,
      recorrencia_numero: formData.recorrencia_numero ? parseInt(formData.recorrencia_numero) : 1,
      observacoes: formData.observacoes,
      tags: formData.tags,
      business_id: formData.isBusiness && user?.business_id ? user.business_id : null,
      nome_business: formData.isBusiness && user?.nome_business ? user.nome_business : null,
      comprovante_url: uploadedUrl,
      cartao_id: formData.cartao_id || null,
      recorrencia_id: formData.recorrencia_id,
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{transaction ? 'Editar Transação' : 'Adicionar Transação'}</h2>
          <button type="button" className="modal-close-btn" onClick={onCancel}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="transaction-form">
          {/* Descrição */}
          <div className="form-group">
            <label htmlFor="description">Descrição *</label>
            <input
              type="text"
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Ex: Conta de luz"
              required
              className="form-control"
            />
          </div>

          {/* Valor + Data */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="value">Valor (R$) *</label>
              <input
                type="number"
                id="value"
                name="value"
                value={formData.value}
                onChange={handleChange}
                required
                step="0.01"
                min="0"
                placeholder="0,00"
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label htmlFor="date">Data *</label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className="form-control"
              />
            </div>
          </div>

          {/* Tipo + Status */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="type">Tipo *</label>
              <select id="type" name="type" value={formData.type} onChange={handleChange} className="form-control">
                <option value="Despesa">📉 Despesa</option>
                <option value="Receita">📈 Receita</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="status">Status *</label>
              <select id="status" name="status" value={formData.status} onChange={handleChange} className="form-control">
                <option value="pago">✅ Efetivado / Pago</option>
                <option value="pendente">⏳ Pendente</option>
              </select>
            </div>
          </div>

          {/* Categoria filtrada e Cartão de Crédito */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">
                Categoria
                {loadingCats && <span className="form-loading"> ⏳</span>}
              </label>
              <select id="category" name="category" value={formData.category} onChange={handleChange} className="form-control">
                <option value="">*Selecione a categoria*</option>
                {filteredCategories.length > 0 ? (
                  filteredCategories.map((cat) => (
                    <option key={cat.id} value={cat.nome}>
                      {cat.nome}
                    </option>
                  ))
                ) : (
                  categories.length > 0
                    ? <option disabled>Nenhuma categoria para este tipo</option>
                    : <option disabled>Carregando categorias...</option>
                )}
              </select>
              {filteredCategories.length === 0 && !loadingCats && (
                <small className="form-hint">
                  Adicione categorias em <strong>🏷️ Categorias</strong> no menu superior.
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="cartao_id">Cartão (Opcional)</label>
              <select id="cartao_id" name="cartao_id" value={formData.cartao_id} onChange={handleChange} className="form-control">
                <option value="">Nenhum</option>
                {cards.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
          </div>

          {/* Observações + Tags */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="observacoes">Obs.</label>
              <input
                type="text"
                id="observacoes"
                name="observacoes"
                placeholder="Nota..."
                value={formData.observacoes}
                onChange={handleChange}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label htmlFor="tags">Tags</label>
              <input
                type="text"
                id="tags"
                name="tags"
                placeholder="lazer, ..."
                value={formData.tags}
                onChange={handleChange}
                className="form-control"
              />
            </div>
          </div>
          
          {/* Anexo - Agora em sua própria linha */}
          <div className="form-group">
            <label>Anexo</label>
            <input type="file" accept="image/*,.pdf" onChange={e => setFile(e.target.files[0])} className="form-control" />
          </div>

          {/* Recorrência - Agora com Qtd de Repetições */}
          <div className="form-group form-recorrente-container">
            <div className="form-recorrente-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <label className="form-check-label" style={{ marginBottom: 0 }}>
                <input
                  type="checkbox"
                  name="recorrente"
                  checked={formData.recorrente}
                  onChange={e => setFormData(p => ({ ...p, recorrente: e.target.checked }))}
                />
                🔁 Recorrência
              </label>
              {formData.recorrente && (
                <select name="frequencia" value={formData.frequencia} onChange={handleChange} className="form-control mini-select" style={{ width: 'auto', padding: '2px 8px' }}>
                  <option value="semanal">Semanal</option>
                  <option value="quinzenal">Quinzenal</option>
                  <option value="mensal">Mensal</option>
                  <option value="anual">Anual</option>
                </select>
              )}
            </div>
            {formData.recorrente && (
              <div className="form-recorrente-details" style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '11px', marginBottom: 2, display: 'block', color: 'var(--text-secondary)' }}>Qtd. Repetições (vazio = ∞)</label>
                  <input
                    type="number"
                    name="recorrencia_total"
                    value={formData.recorrencia_total}
                    onChange={handleChange}
                    placeholder="Ex: 12"
                    min="1"
                    className="form-control"
                    style={{ height: '32px' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '11px', marginBottom: 2, display: 'block', color: 'var(--text-secondary)' }}>Parcela Atual</label>
                  <input
                    type="number"
                    name="recorrencia_numero"
                    value={formData.recorrencia_numero}
                    onChange={handleChange}
                    min="1"
                    className="form-control"
                    style={{ height: '32px' }}
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Informação sobre qual Conta está sendo lançada */}
          <div className="form-group" style={{ marginTop: '2px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)', textAlign: 'left' }}>
              Conta: <strong>{formData.isBusiness ? `🏢 ${user?.nome_business}` : '👤 Pessoal'}</strong>
            </span>
          </div>

          {/* Ações */}
          <div className="modal-footer">
            <button type="button" onClick={onCancel} className="modal-btn modal-btn-cancel">
              Cancelar
            </button>
            <button type="submit" className="modal-btn modal-btn-confirm" disabled={uploading}>
              {uploading ? 'Aguarde...' : (transaction ? 'Atualizar' : 'Adicionar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransactionForm;
